import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "react-error-boundary";
import { ThemeManager } from "@/lib/ThemeManager";
import { LanguageProvider } from "@/lib/i18n";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import LegalHome from "@/pages/LegalHome";
import AuthVerified from "@/pages/AuthVerified";
import AuthResetPassword from "@/pages/AuthResetPassword";
import { APP_SCROLL_ROOT_ID } from "@/lib/scrollToTop";

/**
 * legal-public branch only:
 * - No Landing login / OAuth / authenticated app shell
 * - Public: /, /privacy, /terms
 * - Email landings: /auth/verified, /auth/reset-password (guide back to iOS App)
 */
function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 space-y-4 text-center">
      <h2 className="text-xl font-bold text-red-600">Something went wrong</h2>
      <pre className="p-2 text-xs text-left bg-gray-100 rounded max-w-[300px] overflow-auto">
        {error.message}
      </pre>
      <button
        type="button"
        onClick={resetErrorBoundary}
        className="px-4 py-2 text-primary-foreground bg-primary rounded hover:bg-primary/90"
      >
        Try again
      </button>
    </div>
  );
}

function LegalRouter() {
  return (
    <div
      id={APP_SCROLL_ROOT_ID}
      className="h-[100dvh] overflow-y-auto overflow-x-hidden overscroll-y-none no-scrollbar [-webkit-overflow-scrolling:touch]"
    >
      <Switch>
        <Route path="/privacy" component={Privacy} />
        <Route path="/terms" component={Terms} />
        <Route path="/auth/verified" component={AuthVerified} />
        <Route path="/auth/reset-password" component={AuthResetPassword} />
        <Route component={LegalHome} />
      </Switch>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <TooltipProvider>
            <ThemeManager />
            <div className="h-full overflow-hidden bg-background">
              <LegalRouter />
            </div>
          </TooltipProvider>
        </LanguageProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
